import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { formatProjectName } from '../utils/projectUtils';

export default function Dashboard() {
  const [stats, setStats] = useState({
    activeProjects: 0,
    pendingQuotes: 0,
    totalCustomers: 0,
    totalSuppliers: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Load projects
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      const projects = projectsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Count active projects
      const activeProjects = projects.filter(p => p.status === 'active' || p.status === 'in_progress').length;
      
      // Get recent projects (last 5)
      const sortedProjects = projects
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0);
          const dateB = b.createdAt?.toDate?.() || new Date(0);
          return dateB - dateA;
        })
        .slice(0, 5);
      setRecentProjects(sortedProjects);
      
      // Load quote requests
      const quotesSnapshot = await getDocs(
        query(collection(db, 'quoteRequests'), where('status', '==', 'pending'))
      );
      
      // Load customers
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      
      // Load suppliers
      const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
      
      setStats({
        activeProjects: projects.length,
        pendingQuotes: quotesSnapshot.docs.length,
        totalCustomers: customersSnapshot.docs.length,
        totalSuppliers: suppliersSnapshot.docs.length
      });
      
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active':
      case 'in_progress':
        return 'text-green-600 bg-green-100';
      case 'planning':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'on_hold':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Planning';
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
          <p className="text-gray-600">Welcome to your PFI Connect dashboard</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Active Projects</h3>
            <p className="text-3xl font-bold text-pink-600">{stats.activeProjects}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Pending Quotes</h3>
            <p className="text-3xl font-bold text-orange-600">{stats.pendingQuotes}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Total Customers</h3>
            <p className="text-3xl font-bold text-blue-600">{stats.totalCustomers}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Total Suppliers</h3>
            <p className="text-3xl font-bold text-green-600">{stats.totalSuppliers}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Projects</h3>
            <div className="space-y-3">
              {recentProjects.length > 0 ? (
                recentProjects.map(project => (
                  <div key={project.id} className="flex items-center justify-between">
                    <Link 
                      to={`/projects/${project.id}`}
                      className="text-sm text-gray-900 hover:text-pink-600 truncate flex-1"
                    >
                      {formatProjectName(project)}
                    </Link>
                    <span className={`text-xs px-2 py-1 rounded ml-2 ${getStatusColor(project.status)}`}>
                      {formatStatus(project.status)}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 text-center py-4">
                  No projects yet. 
                  <Link to="/projects/new" className="text-pink-600 hover:text-pink-700 font-medium">
                    Create your first project
                  </Link>
                </div>
              )}
            </div>
            <Link to="/projects" className="text-pink-600 hover:text-pink-700 text-sm font-medium mt-4 inline-block">
              View all projects →
            </Link>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-4">
              <Link to="/projects/new" className="p-4 border border-gray-200 rounded-lg hover:border-pink-300 transition-colors">
                <div className="text-pink-600 font-medium">New Project</div>
                <div className="text-xs text-gray-500">Create a new project</div>
              </Link>
              <Link to="/customers" className="p-4 border border-gray-200 rounded-lg hover:border-pink-300 transition-colors">
                <div className="text-pink-600 font-medium">Manage Customers</div>
                <div className="text-xs text-gray-500">View customer list</div>
              </Link>
              <Link to="/suppliers" className="p-4 border border-gray-200 rounded-lg hover:border-pink-300 transition-colors">
                <div className="text-pink-600 font-medium">Suppliers</div>
                <div className="text-xs text-gray-500">Manage suppliers</div>
              </Link>
              <Link to="/calendar" className="p-4 border border-gray-200 rounded-lg hover:border-pink-300 transition-colors">
                <div className="text-pink-600 font-medium">Calendar</div>
                <div className="text-xs text-gray-500">View schedule</div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}